import { Fragment, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { LinkTarget, NavLink, SocialLink } from './types'
import { PlatformIcon } from './PlatformIcon'
import { SmartLink } from './SmartLink'

export interface FooterWayfinding {
  /** The line that introduces the links, e.g. "Or find your way from here". */
  label: string
  /**
   * Where to go next — the handful of destinations worth naming again at the end of a
   * page. Each is either an in-app route (`to`) or another KKF app (`href`), so this row
   * is where an app hands a visitor on to a sibling.
   */
  links: NavLink[]
}

/**
 * A post in the footer's fourth column.
 *
 * Extends `LinkTarget` rather than requiring `to`: the Foundation owns the blog, so the
 * three venture apps link to it across domains with `href` while the Foundation's own
 * footer uses `to`. `SmartLink` is what knows the difference, and it is why callers can
 * pass either without branching.
 */
export type FooterPost = LinkTarget & {
  image: string
  date: string
  title: string
  /**
   * What the thumbnail shows. Optional, and **empty is the right default** — the picture
   * sits beside the post's own title, so with no alt it is decorative and a screen reader
   * should skip it. It read `alt="Post Image"` until 2026-09-13, which is two words of
   * noise read aloud before every heading in the column.
   */
  imageAlt?: string
}

export interface FooterPreamble {
  /** Small label above the statement. */
  subTitle: string
  title: string
  /** One or more paragraphs — what the Foundation offers, stated plainly. */
  paragraphs: string[]
  /** Optional picture beside the statement. Omit it and the copy takes the full width. */
  image?: string
  /** Alt text for `image`. Describe the picture — do not restate the title. */
  imageAlt?: string
}

/**
 * The install offer at the foot of an installable app.
 *
 * **A store badge renders only when its URL is given**, and both are optional. That is the
 * whole design of this block: Partners is installable from the browser today and has no
 * listing on either store, so it shows the install button and no badges. A badge linking
 * nowhere, or to a store search, is the same mistake as a form that appears to send and does
 * not — it looks like a working path to something that does not exist yet.
 *
 * The two branches are mutually exclusive by construction. `canInstall` is true only where
 * the browser has offered a real prompt; `showIosHint` is true only where it never will, and
 * the offer becomes the Share → Add to Home Screen instruction instead. Neither true means
 * the app is already installed, or the browser cannot install it, and the block sits quiet.
 */
export interface FooterAppPromo {
  title: string
  descr: string
  /** The Android button — Chromium's real install prompt sits behind it. */
  androidLabel: string
  /** The iPhone button. Safari fires no install event, so this reveals the instruction. */
  iosLabel: string
  /** What to do on an iOS browser: Share, then Add to Home Screen. */
  iosHint: string
  /** Rendered only if present. `{ href, image, alt }` per store. */
  stores?: { href: string; image: string; alt: string }[]
  /** True only where the browser has offered a real prompt. */
  canInstall?: boolean
  onInstall?: () => void
}

export interface FooterProps {
  /**
   * The band above the footer proper. Sits on every page because the footer does —
   * there is one `Footer`, mounted in the layout.
   */
  preamble?: FooterPreamble
  /**
   * The card straddling the top of the footer. It used to hold address, email and phone;
   * those live in the Contact Us column below, so this points onward instead.
   */
  wayfinding: FooterWayfinding
  logo: string
  /**
   * The venture's name under the mark, as the header's lockup does it. All four apps share
   * one logo, so a footer without it is a footer that could belong to any of them — and the
   * footer is where somebody checks what site they are on before they leave it.
   */
  logoLabel?: string
  homeTo: string
  about: string
  socials: SocialLink[]
  quickLinks: { title: string; links: NavLink[] }
  /**
   * The fourth column, when an app has one.
   *
   * **Optional.** The Foundation owns the blog, so a venture app's fourth column is
   * either a link out to it or nothing at all — Partners carries nothing, and an
   * empty column headed with a title is worse than three columns. Omit the prop and the
   * column is not rendered; the grid closes up around it.
   */
  recentPosts?: { title: string; posts: FooterPost[] }
  contact: {
    title: string
    /**
     * `href` is optional: a contact line may be a fact rather than a channel. The
     * Foundation's address is "Ulaanbaatar, Mongolia" and its telephone number does not
     * exist yet, and neither is dialable — rendering them as links was how the template's
     * `123-456-7890` ended up looking like a number somebody could ring.
     */
    links: { icon: string; label: string; href?: string }[]
    newsletterPlaceholder: string
    agreementText: string
    /**
     * The link in the newsletter agreement line.
     *
     * A `LinkTarget`, not an `href` string, as of 2026-09-19: the policy is a route of
     * this app now rather than the `'#'` it pointed at since the template landed, and an
     * `<a href="/privacy">` inside a router app is a full page load. `SmartLink` renders
     * whichever is right, so a future app that hosts the policy elsewhere can pass `href`.
     */
    privacyPolicy: LinkTarget & { label: string }
  }
  copyrightName: string
  /**
   * The two links beside the copyright — terms and privacy. `LinkTarget` for the same
   * reason as `contact.privacyPolicy`: they are this app's own routes now.
   */
  bottomNav: (LinkTarget & { label: string })[]
  vectorImage: string
  /**
   * `ink` renders the dark-first footer. It is the hardest surface in the system to
   * darken — ~40 rules painted from four pale grounds — and `.ul-footer--ink` in
   * `_footer.scss` explains at length how nine token remaps cover all of them, and why
   * that is safe here and is not in the header.
   */
  tone?: 'light' | 'ink'
  /**
   * The install offer, under the newsletter column. Omitted — which is every app but the
   * Partners — the footer renders exactly as it did before this existed.
   */
  appPromo?: FooterAppPromo
  onSubscribe?: (email: string) => void
}

/**
 * The site footer. Verified byte-identical across all 19 source pages, so there is
 * exactly one instance, mounted in `SiteLayout`.
 *
 * The copyright year is computed at render time into `#footer-copyright-year`.
 */
export function Footer({
  preamble,
  wayfinding,
  logo,
  logoLabel,
  homeTo,
  about,
  socials,
  quickLinks,
  recentPosts,
  contact,
  copyrightName,
  bottomNav,
  vectorImage,
  tone = 'light',
  appPromo,
  onSubscribe,
}: FooterProps) {
  /** Whether the iPhone instruction is showing. Nothing to install behind it — see below. */
  const [iosHintOpen, setIosHintOpen] = useState(false)

  const handleSubscribe = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const value = new FormData(e.currentTarget).get('email')
    onSubscribe?.(typeof value === 'string' ? value : '')
  }

  return (
    <footer className={tone === 'ink' ? 'ul-footer ul-footer--ink' : 'ul-footer'}>
      {preamble && (
        <div className="ul-footer-preamble">
          <div className="ul-footer-container">
            <div className="ul-footer-preamble-wrapper wow animate__fadeInUp">
              {/* Left column: the picture. Drop `image` and `-content` becomes the only
                  child, which the stylesheet spreads across both columns instead. */}
              {preamble.image && (
                <div className="ul-footer-preamble-media">
                  <img src={preamble.image} alt={preamble.imageAlt ?? ''} loading="lazy" />
                </div>
              )}
              <div className="ul-footer-preamble-content">
                {/* The heading, ruled off from the copy under it. */}
                <div className="ul-footer-preamble-head">
                  <span className="ul-footer-preamble-sub-title">{preamble.subTitle}</span>
                  <h2 className="ul-footer-preamble-title">{preamble.title}</h2>
                </div>
                {/* One column per paragraph on desktop, stacking below sm. */}
                <div className="ul-footer-preamble-body">
                  {preamble.paragraphs.map((paragraph) => (
                    <p className="ul-footer-preamble-txt" key={paragraph}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="ul-footer-top">
        <div className="ul-footer-container">
          <div className="ul-footer-wayfinding">
            <span className="ul-footer-wayfinding-label">{wayfinding.label}</span>
            <div className="ul-footer-wayfinding-links">
              {wayfinding.links.map((link) => (
                <SmartLink key={link.label} to={link.to} href={link.href}>
                  {link.label}
                </SmartLink>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="ul-footer-middle">
        <div className="ul-footer-container">
          <div className="ul-footer-middle-wrapper wow animate__fadeInUp">
            <div className="ul-footer-about">
              {/* The same lockup the header uses — one definition of the mark-plus-name,
                  in `_header.scss`. The label is `aria-hidden` and the alt carries the whole
                  name, so a screen reader hears one name and not "logo" then "Partners". */}
              <Link to={homeTo} className="ul-logo-lockup">
                <img
                  src={logo}
                  alt={logoLabel ? `King of Kings ${logoLabel}` : 'King of Kings'}
                  className="logo"
                />
                {logoLabel && (
                  <span className="ul-logo-lockup-label" aria-hidden="true">
                    {logoLabel}
                  </span>
                )}
              </Link>
              <p className="ul-footer-about-txt">{about}</p>
              <div className="ul-footer-socials">
                {socials.map((s) => (
                  <a key={s.icon} href={s.href} aria-label={s.label ?? s.icon}>
                    <i className={`flaticon-${s.icon}`} />
                  </a>
                ))}
              </div>
            </div>

            <div className="ul-footer-widget">
              <h3 className="ul-footer-widget-title">{quickLinks.title}</h3>

              <div className="ul-footer-widget-links">
                {quickLinks.links.map((link) => (
                  <SmartLink key={link.label} to={link.to} href={link.href}>
                    {link.label}
                  </SmartLink>
                ))}
              </div>
            </div>

            {/* Rendered only when an app supplies one — see the prop. */}
            {recentPosts && (
            <div className="ul-footer-widget ul-footer-recent-posts">
              <h3 className="ul-footer-widget-title">{recentPosts.title}</h3>

              <div className="ul-blog-sidebar-posts">
                {recentPosts.posts.map((post, i) => (
                  <div
                    className="ul-blog-sidebar-post ul-footer-post"
                    key={`${post.to ?? post.href}-${i}`}
                  >
                    <div className="img">
                      <img src={post.image} alt={post.imageAlt ?? ''} />
                    </div>

                    <div className="txt">
                      <span className="date">
                        <span className="icon">
                          <i className="flaticon-calendar" />
                        </span>
                        <span>{post.date}</span>
                      </span>

                      <h4 className="title">
                        <SmartLink to={post.to} href={post.href}>
                          {post.title}
                        </SmartLink>
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            <div className="ul-footer-widget ul-nwsltr-widget">
              <h3 className="ul-footer-widget-title">{contact.title}</h3>
              <div className="ul-footer-widget-links ul-footer-contact-links">
                {contact.links.map((link) =>
                  link.href ? (
                    <a key={link.label} href={link.href}>
                      <i className={`flaticon-${link.icon}`} /> {link.label}
                    </a>
                  ) : (
                    <span key={link.label}>
                      <i className={`flaticon-${link.icon}`} /> {link.label}
                    </span>
                  ),
                )}
              </div>
              <form action="#" className="ul-nwsltr-form" onSubmit={handleSubscribe}>
                <div className="top">
                  <input
                    type="email"
                    name="email"
                    id="nwsltr-email"
                    placeholder={contact.newsletterPlaceholder}
                    className="ul-nwsltr-input"
                  />
                  <button type="submit" aria-label="Subscribe">
                    <i className="flaticon-next" />
                  </button>
                </div>

                <div className="agreement">
                  <label htmlFor="nwsltr-agreement" className="ul-checkbox-wrapper">
                    <input type="checkbox" name="agreement" id="nwsltr-agreement" hidden />
                    <span className="ul-checkbox">
                      <i className="flaticon-tick" />
                    </span>
                    <span className="ul-checkbox-txt">
                      {contact.agreementText}{' '}
                      <SmartLink to={contact.privacyPolicy.to} href={contact.privacyPolicy.href}>
                        {contact.privacyPolicy.label}
                      </SmartLink>
                    </span>
                  </label>
                </div>
              </form>

              {appPromo && (
                <div className="ul-footer-app">
                  <p className="ul-footer-app-title">{appPromo.title}</p>
                  <p className="ul-footer-app-descr">{appPromo.descr}</p>
                  {/*
                    One button per platform, and **both do something**.

                    Android sits on Chromium's real `beforeinstallprompt`, so pressing it
                    opens the browser's own install dialog. iPhone cannot: Safari fires no
                    event and offers no API, so that button reveals the Share → Add to Home
                    Screen instruction, which is the actual mechanism on that platform.

                    The Android button is disabled rather than hidden when the browser has
                    not offered a prompt — already installed, or a browser that cannot. A
                    button that appears and disappears depending on state nobody can see is
                    worse than one that is visibly unavailable, and `title` says which.
                  */}
                  <div className="ul-footer-app-platforms">
                    <button
                      type="button"
                      className="ul-footer-app-platform"
                      onClick={appPromo.onInstall}
                      disabled={!appPromo.canInstall}
                      title={appPromo.canInstall ? undefined : appPromo.iosHint}
                    >
                      <PlatformIcon platform="android" />
                      {appPromo.androidLabel}
                    </button>

                    <button
                      type="button"
                      className="ul-footer-app-platform"
                      aria-expanded={iosHintOpen}
                      onClick={() => setIosHintOpen((open) => !open)}
                    >
                      <PlatformIcon platform="ios" />
                      {appPromo.iosLabel}
                    </button>
                  </div>

                  {iosHintOpen && <p className="ul-footer-app-hint">{appPromo.iosHint}</p>}

                  {/* No badge without a real store URL — see `FooterAppPromo`. */}
                  {appPromo.stores && appPromo.stores.length > 0 && (
                    <div className="ul-footer-app-actions">
                      {appPromo.stores.map((store) => (
                        <a
                          key={store.href}
                          href={store.href}
                          className="ul-footer-app-store"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <img src={store.image} alt={store.alt} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* footer bottom */}
      <div className="ul-footer-bottom">
        <div className="ul-footer-container">
          <div className="ul-footer-bottom-wrapper">
            <p className="copyright-txt">
              &copy; <span id="footer-copyright-year">{new Date().getFullYear()}</span> {copyrightName}
            </p>
            {/* These anchors are inline and separated by a literal space, so no wrapper
                element may be added here or the gap between them changes. */}
            <div className="ul-footer-bottom-nav">
              {bottomNav.map((item, i) => (
                <Fragment key={item.label}>
                  {i > 0 ? ' ' : null}
                  <SmartLink to={item.to} href={item.href}>
                    {item.label}
                  </SmartLink>
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* vector */}
      <div className="ul-footer-vectors">
        <img src={vectorImage} alt="Footer Image" className="ul-footer-vector-1" />
      </div>
    </footer>
  )
}
